import { faListCheck, faRotateRight } from '@fortawesome/pro-regular-svg-icons'
import { FormikProps } from 'formik'
import { get, isEmpty } from 'lodash'
import { useCallback, useEffect } from 'react'
import {
  RadioButtonGroupMapping,
  RadioButtonType,
} from '@labrav/react-components'
import { Divider } from '@mui/material'
import { SubscriptionSectionWrapper } from '../../SubscriptionSectionWrapper/SubscriptionSectionWrapper'
import { RecipientCompanyDetail } from '../RecipientCompanyDetail/RecipientCompanyDetail'
import { EulaSection } from '../../../../common/components/EulaSection/EulaSection'
import { AdditionalUsageDimensionsV2 } from '../../../../common/components/AdditionalUsageDimensionsV2/AdditionalUsageDimensionsV2'
import { PaymentSchedule } from '../PaymentSchedule/PaymentSchedule'
import { useStyles } from './RenewSubscriptionForm.styles'
import { ServiceDate } from '../ServiceDate/ServiceDate'
import type { AgreementBasedOffer } from '../../../../modules/subscription/reducer'
import { Dimension } from '../Dimension/Dimension'
import { defaultNow } from '../../../../common/utils/dateTimeNow'
import { EulaType } from '../../../../types/flyout'
import { RenewalSection } from '../RenewalSection/RenewalSection'
import { getCurrencySymbol } from '../../../../common/utils/getCurrencySymbol'
import { useSelector } from '../../../../store'
import type { FlyOutListingData } from '../../../../types/flyout'
import { NetPaymentTermsField } from '../../../../common/components/NetPaymentTermsField/NetPaymentTermsField'
import { MultiCurrencyForm } from '../../../../common/components/MultiCurrencyForm/MultiCurrencyForm'
import clsx from 'clsx'

export const eulaButtonOptionsV2: RadioButtonGroupMapping[] = [
  {
    key: EulaType.STANDARD,
    label: 'Standard contract for AWS marketplace (SCMP)',
    type: RadioButtonType.STANDARD,
    infoDescription:
      'SCMP is a standardized contract template intended to help streamline procurement and speed up transactions.',
  },
  {
    key: EulaType.CUSTOM,
    label: 'Custom EULA',
    type: RadioButtonType.STANDARD,
    infoDescription:
      'Provide a URL to a custom end-user license agreement (EULA) for your product.',
  },
]

export const RenewSubscriptionForm = ({
  formikProps,
  productId,
  subscriptionId,
}: {
  formikProps: FormikProps<AgreementBasedOffer>
  productId: string
  subscriptionId: string
}) => {
  const { classes } = useStyles()
  const { values, touched, setFieldTouched, validateField } = formikProps
  const additionalUsageObjects = (values.dimensions || []).filter(
    ({ isAdditionalUsageAllowed }) => isAdditionalUsageAllowed
  )
  const flyoutSellerInfo = useSelector<FlyOutListingData>(
    state => state.flyoutSellerInfo
  )

  const validateInstallments = useCallback(async () => {
    values.installments?.forEach(async (_installmentItem, index) => {
      if (
        !isEmpty(values?.offerExpirationDate) &&
        !get(touched, `installments.${index}.paymentDate`, false)
      ) {
        await setFieldTouched(`installments.${index}.paymentDate`, true)
      }
    })
    await validateField('installments')
  }, [
    values?.offerExpirationDate,
    values.installments,
    touched,
    setFieldTouched,
    validateField,
  ])

  useEffect(() => {
    if (
      !isEmpty(values?.offerExpirationDate) ||
      !isEmpty(values?.subscriptionEndDate)
    ) {
      validateInstallments()
    }
  }, [
    values?.offerExpirationDate,
    values?.subscriptionEndDate,
    values.installments,
    validateInstallments,
  ])

  const currencySymbol = getCurrencySymbol(values?.currency as string)

  return (
    <div className={classes.rootForm}>
      <SubscriptionSectionWrapper
        children={<RecipientCompanyDetail />}
        heading="Recipient company details"
        icon={faListCheck}
      />
      <SubscriptionSectionWrapper
        children={
          <>
            <MultiCurrencyForm
              formik={
                formikProps as unknown as FormikProps<Record<string, unknown>>
              }
              flyoutSellerInfo={flyoutSellerInfo}
            />
            <Divider className={clsx(classes.divider, classes.dividerStyle)} />
            <NetPaymentTermsField
              context="offer"
              formik={
                formikProps as unknown as FormikProps<Record<string, unknown>>
              }
            />
          </>
        }
        heading="Currency and billing configuration"
        icon={faListCheck}
      />
      <SubscriptionSectionWrapper
        children={<ServiceDate subscriptionId={subscriptionId} />}
        heading="Service dates"
        icon={faListCheck}
      />
      {values.flexiblePaymentTerms ? (
        <SubscriptionSectionWrapper
          children={
            <PaymentSchedule
              subscriptionId={subscriptionId}
              currencySymbol={currencySymbol}
            />
          }
          heading="Payment schedule"
          icon={faListCheck}
        />
      ) : (
        <></>
      )}
      <SubscriptionSectionWrapper
        children={
          <Dimension productId={productId} isAgreementBasedOfferRenewal />
        }
        heading="Dimensions"
        icon={faListCheck}
      />
      {additionalUsageObjects.length > 0 ? (
        <SubscriptionSectionWrapper
          children={
            <AdditionalUsageDimensionsV2
              formik={
                formikProps as unknown as FormikProps<Record<string, unknown>>
              }
            />
          }
          heading="Additional usage"
          icon={faListCheck}
        />
      ) : (
        <></>
      )}
      <SubscriptionSectionWrapper
        children={
          <RenewalSection
            formik={
              formikProps as unknown as FormikProps<Record<string, unknown>>
            }
          />
        }
        heading={'Renewal'}
        icon={faRotateRight}
      />
      <EulaSection
        defaultNow={defaultNow}
        formik={formikProps as unknown as FormikProps<Record<string, unknown>>}
        fileUploadDescription={
          "Provide a .pdf or .txt file for the end-user license agreement (EULA). (File can't exceed 5 MB.)"
        }
        fileUploadButtonText={'Upload file'}
        fileUploadContainerTitle={'Drag and drop file here'}
        quantity={1}
        eulaOptionsHeading={'Custom EULA'}
        showEulaFileUpload={values?.eulaType === EulaType.CUSTOM}
        eulaOptions={eulaButtonOptionsV2}
        fileSize={5}
      />
    </div>
  )
}
